const TERMINAL_STATUSES = new Set(["SAVED", "DISCARDED"]);

function revision(value) {
  const number = Number(value || 0);
  return Number.isSafeInteger(number) && number >= 0 ? number : 0;
}

function leaseEpoch(record) {
  return revision(record?.leaseEpoch);
}

function acknowledgedRevision(record) {
  return Math.min(revision(record?.ackedRevision), revision(record?.localRevision));
}

function acknowledgementTime(record) {
  const value = record?.lastAcknowledgedAt || record?.result?.serverSavedAt;
  const time = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(time) ? time : 0;
}

export function executionStatus(record) {
  return record?.document?.status || record?.status || null;
}

export function isTerminalExecution(record) {
  return TERMINAL_STATUSES.has(executionStatus(record));
}

// Which candidate's document is canonical. SAVED and DISCARDED are terminal:
// a terminal candidate is never replaced by ACTIVE/FINISHED content, whatever
// revision a stale editor reached. Otherwise the higher revision wins; at an
// equal revision the durable record wins because, with one writer per
// execution, equal revisions carry equal content and the durable copy is the
// one the sync engine sends and acknowledges.
function contentWinner(candidate, durable) {
  const candidateTerminal = isTerminalExecution(candidate);
  if (candidateTerminal !== isTerminalExecution(durable)) {
    return candidateTerminal ? candidate : durable;
  }
  return revision(candidate.localRevision) > revision(durable.localRevision)
    ? candidate
    : durable;
}

// Sync metadata (ackedRevision, lastAcknowledgedAt, syncState, lastSyncError)
// travels as one unit. The losing candidate may contribute it only when its
// whole revision is strictly older than the chosen content: that ACK then names
// an ancestor revision and can never mark the chosen document itself as
// acknowledged.
function metadataSource(winner, loser) {
  if (revision(loser.localRevision) >= revision(winner.localRevision)) return winner;
  const winnerAck = acknowledgedRevision(winner);
  const loserAck = acknowledgedRevision(loser);
  if (loserAck !== winnerAck) return loserAck > winnerAck ? loser : winner;
  const winnerTime = acknowledgementTime(winner);
  const loserTime = acknowledgementTime(loser);
  if (loserTime !== winnerTime) return loserTime > winnerTime ? loser : winner;
  return winner;
}

function metadataValue(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

/**
 * Returns the one coherent local record for an execution.
 *
 * `candidate` is what a tab holds or wants to write (memory, an incoming put);
 * `durable` is the record read from IndexedDB. The result's document,
 * localRevision, ackedRevision, pending and syncState always describe the same
 * content; acknowledgement metadata is never transplanted onto a document the
 * server did not accept.
 */
export function reconcileExecutionRecord(candidate, durable) {
  if (!candidate) return durable;
  if (!durable) return candidate;
  if (
    candidate.executionId !== durable.executionId ||
    candidate.userId !== durable.userId
  ) {
    return durable;
  }

  const candidateEpoch = leaseEpoch(candidate);
  const durableEpoch = leaseEpoch(durable);
  if (candidateEpoch !== durableEpoch) {
    return candidateEpoch > durableEpoch ? candidate : durable;
  }

  const winner = contentWinner(candidate, durable);
  const loser = winner === candidate ? durable : candidate;
  const metadata = metadataSource(winner, loser);
  let localRevision = revision(winner.localRevision);

  // A pending terminal document that displaces a durable ACTIVE/FINISHED
  // record at an equal or higher revision must be pushed above that revision:
  // the server may already hold the non-terminal document there and would
  // otherwise treat the terminal push as a stale replay. This never applies to
  // a rejected stale write (a non-terminal candidate never wins over a durable
  // terminal record), so rejection invents no revision.
  if (
    winner === candidate &&
    isTerminalExecution(winner) &&
    !isTerminalExecution(loser) &&
    revision(loser.localRevision) >= localRevision &&
    acknowledgedRevision(winner) < localRevision
  ) {
    localRevision = revision(loser.localRevision) + 1;
  }

  if (metadata === winner && localRevision === revision(winner.localRevision)) {
    return winner;
  }

  const ackedRevision = Math.min(localRevision, acknowledgedRevision(metadata));
  const pending = localRevision > ackedRevision;
  let syncState = metadataValue(metadata, "syncState");
  if (pending && syncState === "SYNCED") syncState = "PENDING";
  if (!pending && syncState === "PENDING") syncState = "SYNCED";

  return {
    ...winner,
    localRevision,
    ackedRevision,
    pending,
    pendingKey: pending ? 1 : 0,
    syncState,
    lastAcknowledgedAt: metadataValue(metadata, "lastAcknowledgedAt"),
    lastSyncError: metadataValue(metadata, "lastSyncError"),
  };
}

export { revision as executionRevision };
